/**
 * client/src/crm/api.ts — the only place this module talks to the network.
 *
 * Three rules.
 *
 * 1. **`credentials: 'same-origin'` on every request.** Auth is a same-origin
 *    httponly cookie (`vb_session`). There is no token in JS to attach, and
 *    there must never be one: an httponly cookie is unreadable to script, which
 *    is the whole point. `'same-origin'` (not `'include'`) because the API is
 *    served from this origin and a cross-origin credential send is not a thing
 *    this app should be able to do by accident.
 *
 * 2. **Never invent a value on failure.** A failed call throws `ApiError`. It
 *    does not return `0`, `[]`, or a default object. Unknown is not zero
 *    (provenance law 3), and a UI that renders a fabricated empty state as if
 *    it were data is how a rep tells a client something untrue.
 *
 * 3. **No derivation.** These functions parse JSON and hand it to React. They do
 *    not sort by score, compute totals, or reformat money or dates. Filtering
 *    and paging are expressed as QUERY PARAMETERS so the server does the work
 *    and the client stays a renderer (ADR-0002).
 */

import type {
  CrmAiStatus,
  EngagementRollup,
  NewReportLink,
  Report,
  ReportHistory,
  ReportLink,
  ReportLinkList,
  ReportPage,
  ReportSubjectType,
  UnlinkResult,
  Activity,
  ActivityPage,
  ActivityType,
  AllowedTransitions,
  ApiErrorBody,
  CrmStatus,
  Deadline,
  DeadlineKind,
  DeadlinePage,
  Engagement,
  EngagementKind,
  EngagementPage,
  EngagementPartyLink,
  EngagementStatus,
  Fact,
  FactDecision,
  FactPage,
  FactStatus,
  FactSubjectType,
  FlowCatalogue,
  LinkList,
  NewActivity,
  NewEngagementPartyLink,
  NewFact,
  NewParcel,
  NewParcelPartyLink,
  NewParty,
  Parcel,
  ParcelPage,
  ParcelPatch,
  ParcelPartyLink,
  Party,
  PartyPage,
  PartyPatch,
  QueueStats,
  Task,
  TaskPage,
  TaskState,
  TransitionRequest,
  TransitionResult,
} from './types';
import type { ProtestOutcome, ProtestPortfolio } from './protestTypes';

/** Mount point from `crm_next.router`: `APIRouter(prefix="/api/next/crm")`. */
export const CRM_API_BASE = '/api/next/crm';

/**
 * Where this island is mounted in the browser's URL space — it must match the
 * `<Route path="/crm/*">` the owner adds to `client/src/App.tsx` (see README.md).
 *
 * It lives in this file, next to `CRM_API_BASE`, so that the three screens can
 * build links from one constant without importing `CrmApp` (which imports them,
 * and a module cycle in a lazily-loaded chunk is a bad way to spend an
 * afternoon). Change the route and change this together.
 */
export const CRM_BASE_PATH = '/crm';

/**
 * EVERY RECORD THIS ORG HAS, AS ONE ARCHIVE — G11, and a URL rather than a fetch.
 *
 * `<a href download>` and not `request()`: the browser streams the archive straight to disk with
 * the session cookie it already has, so a large export never passes through this app's memory and
 * a slow one shows the browser's own progress instead of a spinner we would have to invent.
 *
 * NOT BEHIND THE FEATURE FLAG, on the server or here. The standing rule is that tenant isolation,
 * deadline accuracy and data export are never gated — safety is not upsell — so the route sits
 * beside `/status` rather than on the flagged router, and this link is offered whether or not the
 * screens that draw these records are switched on.
 */
export const CRM_EXPORT_URL = `${CRM_API_BASE}/export`;

/* --------------------------------------------------------------------------- *
 * ApiError                                                                     *
 * --------------------------------------------------------------------------- */

/**
 * A non-2xx response, carrying enough for the UI to say something true.
 *
 * `status` matters more than usual here, because this API uses status codes
 * deliberately:
 *   - **404** means "no such thing IN YOUR ORG" *and also* "the feature flag is
 *     off". Both are answered with 404 on purpose: a disabled module must not
 *     be an oracle for whether an endpoint exists, and a foreign id must not be
 *     an oracle for whether another tenant's row exists. So a 404 here is never
 *     proof that a row does not exist anywhere — do not phrase it that way.
 *   - **409** is either a `Conflict` or an `invalid_transition` (which names
 *     both states and the legal moves — see `invalidTransition()`).
 *   - **405** on `DELETE /activities/{id}` is the append-only rule (N3), not a
 *     bug.
 *   - **401** means the session cookie is missing or expired.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly body: ApiErrorBody | null;
  /** The server's `error` slug when present ("not_found", "conflict", ...). */
  readonly code: string | null;

  constructor(
    status: number,
    statusText: string,
    url: string,
    body: ApiErrorBody | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.body = body;
    this.code = body && typeof body.error === 'string' ? body.error : null;
    // Restores the prototype chain when compiled to ES5 targets, so
    // `err instanceof ApiError` stays true.
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * The `invalid_transition` payload, or null if this error is something else.
 * Lets a screen render "you cannot go from `filed` to `settled`; from `filed`
 * you may go to `informal`, `withdrawn` or `lost`" using the SERVER's list of
 * legal moves rather than a client-side copy of SPEC section 4.
 */
export function invalidTransition(err: unknown): {
  kind: string;
  from: string | null;
  to: string | null;
  allowed: string[];
  message: string;
} | null {
  if (!(err instanceof ApiError) || err.code !== 'invalid_transition' || !err.body) {
    return null;
  }
  const body = err.body;
  return {
    kind: typeof body.kind === 'string' ? body.kind : '',
    from: typeof body.from === 'string' ? body.from : null,
    to: typeof body.to === 'string' ? body.to : null,
    allowed: Array.isArray(body.allowed) ? body.allowed : [],
    message: typeof body.message === 'string' ? body.message : err.message,
  };
}

/** A human-readable line for any thrown value, without inventing detail. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

/* --------------------------------------------------------------------------- *
 * Transport                                                                    *
 * --------------------------------------------------------------------------- */

type QueryValue = string | number | boolean | null | undefined;

/**
 * Build a query string, dropping null/undefined. Empty string is dropped too:
 * `?q=` would be an empty search filter rather than "no filter", and the
 * difference shows up as an empty result set the user did not ask for.
 */
function queryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

async function readBody(response: Response): Promise<ApiErrorBody | null> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as ApiErrorBody;
  } catch {
    // A non-JSON error body (an HTML 502 page from a proxy, say). Keep the raw
    // text rather than discarding it: "<!doctype html>..." in a message is ugly
    // but it is the truth, and it is how you diagnose a bad deploy.
    return { message: text.slice(0, 500) };
  }
}

function messageFor(response: Response, body: ApiErrorBody | null): string {
  if (body) {
    if (typeof body.message === 'string' && body.message) return body.message;
    if (typeof body.detail === 'string' && body.detail) return body.detail;
    if (Array.isArray(body.detail) && body.detail.length > 0) {
      // FastAPI 422. Do not try to pretty-print pydantic's structure here; show
      // that it was a validation error and let the console carry the detail.
      return `request rejected as invalid (${body.detail.length} problem(s))`;
    }
  }
  return `${response.status} ${response.statusText}`;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

/**
 * One fetch, one JSON parse, one error class. Everything below goes through it.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const url = `${CRM_API_BASE}${path}${options.query ? queryString(options.query) : ''}`;

  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(options.body);
  }

  const response = await fetch(url, {
    method,
    headers,
    body: payload,
    // Rule 1. The session cookie is httponly; this is how it gets sent.
    credentials: 'same-origin',
    signal: options.signal,
  });

  if (!response.ok) {
    const body = await readBody(response);
    throw new ApiError(response.status, response.statusText, url, body, messageFor(response, body));
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as unknown as T;
  }
  return JSON.parse(text) as T;
}

/* --------------------------------------------------------------------------- *
 * Status — the server-driven feature flag                                      *
 * --------------------------------------------------------------------------- */

/**
 * `GET /status`. The one route that answers whether the module is switched on.
 *
 * It is deliberately NOT flag-gated, so the client never has to probe a 404 to
 * learn the answer — a 404 cannot distinguish "disabled" from "old build" from
 * "typo in the path". A 401 from here means "not logged in", not "no CRM".
 */
export function getStatus(signal?: AbortSignal): Promise<CrmStatus> {
  return request<CrmStatus>('/status', { signal });
}

/* --------------------------------------------------------------------------- *
 * Parties                                                                      *
 * --------------------------------------------------------------------------- */

export interface ListPartiesParams {
  q?: string;
  kind?: string;
  limit?: number;
  offset?: number;
}

export function listParties(
  params: ListPartiesParams = {},
  signal?: AbortSignal,
): Promise<PartyPage> {
  return request<PartyPage>('/parties', { query: { ...params }, signal });
}

export function getParty(partyId: string, signal?: AbortSignal): Promise<Party> {
  return request<Party>(`/parties/${encodeURIComponent(partyId)}`, { signal });
}

/**
 * `POST /parties`. The org is never in the body: the server derives it from the session cookie and
 * `PartyIn` forbids extra keys, so a caller cannot name one even by accident.
 */
export function createParty(body: NewParty, signal?: AbortSignal): Promise<Party> {
  return request<Party>('/parties', { method: 'POST', body, signal });
}

/** `PATCH /parties/{id}` — an absent key leaves a field alone; an explicit null clears it. */
export function patchParty(
  partyId: string,
  body: PartyPatch,
  signal?: AbortSignal,
): Promise<Party> {
  return request<Party>(`/parties/${encodeURIComponent(partyId)}`, {
    method: 'PATCH', body, signal,
  });
}

/* --------------------------------------------------------------------------- *
 * Parcels                                                                      *
 * --------------------------------------------------------------------------- */

export interface ListParcelsParams {
  q?: string;
  state?: string;
  county?: string;
  prop_id?: string;
  limit?: number;
  offset?: number;
}

export function listParcels(
  params: ListParcelsParams = {},
  signal?: AbortSignal,
): Promise<ParcelPage> {
  return request<ParcelPage>('/parcels', { query: { ...params }, signal });
}

export function getParcel(parcelId: string, signal?: AbortSignal): Promise<Parcel> {
  return request<Parcel>(`/parcels/${encodeURIComponent(parcelId)}`, { signal });
}

export function listParcelParties(
  parcelId: string,
  signal?: AbortSignal,
): Promise<LinkList<ParcelPartyLink>> {
  // The server types this as LinkList[dict]; the rows are ParcelPartyOut plus a
  // denormalised `party`. Asserted here, once, rather than at every call site.
  return request<LinkList<ParcelPartyLink>>(
    `/parcels/${encodeURIComponent(parcelId)}/parties`,
    { signal },
  );
}

/** `POST /parcels`. A duplicate `(state, county, prop_id)` comes back 409 and is shown in words. */
export function createParcel(body: NewParcel, signal?: AbortSignal): Promise<Parcel> {
  return request<Parcel>('/parcels', { method: 'POST', body, signal });
}

/** `PATCH /parcels/{id}`. */
export function patchParcel(
  parcelId: string,
  body: ParcelPatch,
  signal?: AbortSignal,
): Promise<Parcel> {
  return request<Parcel>(`/parcels/${encodeURIComponent(parcelId)}`, {
    method: 'PATCH', body, signal,
  });
}

/**
 * `POST /parcels/{id}/parties` — link an existing party to a parcel with a role. Both ends are
 * re-resolved in the caller's org on the server, so another tenant's id is a 404.
 */
export function linkParcelParty(
  parcelId: string,
  body: NewParcelPartyLink,
  signal?: AbortSignal,
): Promise<ParcelPartyLink> {
  return request<ParcelPartyLink>(`/parcels/${encodeURIComponent(parcelId)}/parties`, {
    method: 'POST', body, signal,
  });
}

/* --------------------------------------------------------------------------- *
 * Engagements                                                                  *
 * --------------------------------------------------------------------------- */

export interface ListEngagementsParams {
  kind?: EngagementKind;
  status?: EngagementStatus;
  parcel_id?: string;
  open_only?: boolean;
  limit?: number;
  offset?: number;
}

export function listEngagements(
  params: ListEngagementsParams = {},
  signal?: AbortSignal,
): Promise<EngagementPage> {
  return request<EngagementPage>('/engagements', { query: { ...params }, signal });
}

/**
 * `POST /engagements` — start a new piece of work.
 *
 * NO `status` FIELD, AND THAT IS THE SERVER'S RULE NOT AN OMISSION HERE. `schemas.EngagementIn`
 * deliberately has none: "a new engagement starts at its kind's initial state and moves only
 * through `/transition`, so the audit trail has no holes." A client that could post a status could
 * create a record that arrived already three states along with nothing in its history saying how.
 *
 * `amount` IS A STRING ON THE WAY IN AS WELL AS OUT. The column is NUMERIC(14,2) and the schema's
 * own comment says why a JSON float will not do: "12500.00" round-trips and 0.1+0.2 does not. What
 * the person typed is what is sent — this client neither parses it nor formats it, on either leg.
 */
export interface NewEngagement {
  title: string;
  kind: EngagementKind;
  parcel_id?: string | null;
  tax_year?: number | null;
  /** A decimal STRING or omitted. Never a number, and never "0" standing in for unknown. */
  amount?: string | null;
  /** Three letters. The server defaults it to USD when absent. */
  currency?: string;
  notes?: string | null;
}

export function createEngagement(body: NewEngagement, signal?: AbortSignal): Promise<Engagement> {
  return request<Engagement>('/engagements', { method: 'POST', body, signal });
}

export function getEngagement(engagementId: string, signal?: AbortSignal): Promise<Engagement> {
  return request<Engagement>(`/engagements/${encodeURIComponent(engagementId)}`, { signal });
}

/**
 * `PATCH /engagements/{id}` — correct a field on an engagement.
 *
 * THIS EXISTED THE WHOLE TIME. `EngagementTable`'s header used to say inline editing was impossible
 * because "the API does not have it: `crm/api.ts` exposes no PATCH for an engagement." That was
 * true of THIS FILE and false of the server — `router.py` has had `@gated.patch("/engagements/
 * {engagement_id}")` and `schemas.EngagementPatch` all along. A missing client function had been
 * written up as a product constraint.
 *
 * WHAT IT WILL NOT TAKE: `status`. Status moves only through `/transition`, which checks the move
 * is legal and writes the activity row in the same transaction. A PATCH that could set status would
 * be a hole in the history, which is why the server's own patch schema has no such field.
 *
 * `amount` is a decimal STRING, exactly as it is everywhere else in this client.
 */
export interface EngagementPatch {
  title?: string;
  parcel_id?: string | null;
  tax_year?: number | null;
  amount?: string | null;
  currency?: string | null;
  notes?: string | null;
}

export function updateEngagement(
  engagementId: string,
  body: EngagementPatch,
  signal?: AbortSignal,
): Promise<Engagement> {
  return request<Engagement>(`/engagements/${encodeURIComponent(engagementId)}`, {
    method: 'PATCH', body, signal,
  });
}

export function listEngagementParties(
  engagementId: string,
  signal?: AbortSignal,
): Promise<LinkList<EngagementPartyLink>> {
  return request<LinkList<EngagementPartyLink>>(
    `/engagements/${encodeURIComponent(engagementId)}/parties`,
    { signal },
  );
}

/** `POST /engagements/{id}/parties` — link an existing party to an engagement with a role. */
export function linkEngagementParty(
  engagementId: string,
  body: NewEngagementPartyLink,
  signal?: AbortSignal,
): Promise<EngagementPartyLink> {
  return request<EngagementPartyLink>(
    `/engagements/${encodeURIComponent(engagementId)}/parties`,
    { method: 'POST', body, signal },
  );
}

/**
 * `GET /engagements/{id}/transitions` — what this engagement may legally do next.
 *
 * Call this instead of hard-coding a copy of the state machine. The server's
 * answer is the buttons; anything else is the client guessing and earning a 409.
 */
export function listAllowedTransitions(
  engagementId: string,
  signal?: AbortSignal,
): Promise<AllowedTransitions> {
  return request<AllowedTransitions>(
    `/engagements/${encodeURIComponent(engagementId)}/transitions`,
    { signal },
  );
}

/**
 * `POST /engagements/{id}/transition`. Returns the engagement AND the
 * `status_change` activity, because both were written in the same transaction.
 */
export function transitionEngagement(
  engagementId: string,
  body: TransitionRequest,
  signal?: AbortSignal,
): Promise<TransitionResult> {
  return request<TransitionResult>(
    `/engagements/${encodeURIComponent(engagementId)}/transition`,
    { method: 'POST', body, signal },
  );
}

/** `GET /flows` — the whole catalogue, for pipeline UIs. */
export function listFlows(signal?: AbortSignal): Promise<FlowCatalogue> {
  return request<FlowCatalogue>('/flows', { signal });
}

/* --------------------------------------------------------------------------- *
 * Activities                                                                   *
 * --------------------------------------------------------------------------- */

export interface ListActivitiesParams {
  type?: ActivityType;
  party_id?: string;
  parcel_id?: string;
  engagement_id?: string;
  open_tasks?: boolean;
  limit?: number;
  offset?: number;
}

export function listActivities(
  params: ListActivitiesParams = {},
  signal?: AbortSignal,
): Promise<ActivityPage> {
  return request<ActivityPage>('/activities', { query: { ...params }, signal });
}

/**
 * `POST /activities/{id}/complete` — mark a task done.
 *
 * NO BODY, AND THAT IS THE FEATURE. `PATCH /activities/{id}` accepts `completed_at`, so this client
 * could send its own `new Date()`. It should not. A completion time lands in an append-only record
 * a firm may later rely on in a dispute, and a laptop with a wrong clock, a wrong timezone, or a
 * user who simply changed it is not an acceptable authority for it. The server holds the clock, so
 * the server writes the time; there is nothing here for a client to get wrong.
 *
 * The PATCH route still takes `completed_at` for the case this one deliberately does not cover:
 * recording that something was finished at a time OTHER than now, which is a person stating a fact
 * and is allowed to be one.
 *
 * A `status_change` row answers 409 — it is an audit record and the repository refuses to touch it.
 */
export function completeActivity(activityId: string, signal?: AbortSignal): Promise<Activity> {
  return request<Activity>(`/activities/${encodeURIComponent(activityId)}/complete`, {
    method: 'POST', signal,
  });
}

export function createActivity(body: NewActivity, signal?: AbortSignal): Promise<Activity> {
  return request<Activity>('/activities', { method: 'POST', body, signal });
}

/**
 * There is intentionally NO `deleteActivity`. `DELETE /activities/{id}` answers
 * 405: the activity log is the append-only spine (charter N3) and a CRM that
 * lets a rep erase a call is a CRM whose history cannot be trusted in a dispute.
 * Mark it completed with PATCH instead.
 */

/* --------------------------------------------------------------------------- *
 * Deadlines (N6)                                                               *
 * --------------------------------------------------------------------------- */

export interface ListDeadlinesParams {
  kind?: string;
  jurisdiction?: string;
  tax_year?: number;
  parcel_id?: string;
  engagement_id?: string;
  open_only?: boolean;
  limit?: number;
  offset?: number;
}

export function listDeadlines(
  params: ListDeadlinesParams = {},
  signal?: AbortSignal,
): Promise<DeadlinePage> {
  return request<DeadlinePage>('/deadlines', { query: { ...params }, signal });
}

export function getDeadline(deadlineId: string, signal?: AbortSignal): Promise<Deadline> {
  return request<Deadline>(`/deadlines/${encodeURIComponent(deadlineId)}`, { signal });
}

/**
 * `POST /deadlines` — the way a date gets INTO the product (2026-09-07).
 *
 * IT HAS BEEN ON THE SERVER THE WHOLE TIME, with full CRUD, a required `source` and a vocabulary
 * check, and this client never called it. So the record N6 calls the category position — "one
 * missed window costs a client a full tax year" — could only be created by somebody with a shell
 * and a cookie, which means the deadlines screen and its new calendar were, for every real user, a
 * permanently empty table.
 *
 * `source` IS REQUIRED AND HAS NO DEFAULT, on the server (`min_length=3`) and in the form. The
 * schema's own comment is the reason: "a deadline is entered or sourced and never computed by a
 * model. 'Tex. Tax Code 41.44(a)(1)' is a source; '' is a liability." A form that pre-filled it, or
 * offered "same as last time", would be this product asserting a legal date on nobody's authority
 * and calling it sourced.
 *
 * `due_date` IS A CALENDAR DATE, sent as the `YYYY-MM-DD` the input produced. Nothing here builds a
 * `Date` out of it — see `labels.calendarParts` for the day this product lost to doing that.
 */
export interface NewDeadline {
  jurisdiction: string;
  kind: DeadlineKind;
  /** `YYYY-MM-DD`, exactly as typed. Never an instant, never a zone. */
  due_date: string;
  /** Where this date came from. Required, and never invented. */
  source: string;
  tax_year?: number | null;
  parcel_id?: string | null;
  engagement_id?: string | null;
  notes?: string | null;
}

export function createDeadline(body: NewDeadline, signal?: AbortSignal): Promise<Deadline> {
  return request<Deadline>('/deadlines', { method: 'POST', body, signal });
}

/**
 * `PATCH /deadlines/{id}` — including the one field that matters most, `satisfied_at`.
 *
 * MARKING A DEADLINE SATISFIED IS A CLAIM ABOUT A DATE, not a checkbox. `satisfied_at` is WHEN it
 * was met, and a control that quietly stamped "now" would record the day somebody got round to
 * ticking it rather than the day the protest was filed. The caller passes the date; the screen asks
 * for it.
 */
export interface DeadlinePatch {
  jurisdiction?: string;
  kind?: DeadlineKind;
  due_date?: string;
  source?: string;
  tax_year?: number | null;
  parcel_id?: string | null;
  engagement_id?: string | null;
  /** An ISO instant, or `null` to say it is not satisfied after all. */
  satisfied_at?: string | null;
  notes?: string | null;
}

export function patchDeadline(
  deadlineId: string,
  patch: DeadlinePatch,
  signal?: AbortSignal,
): Promise<Deadline> {
  return request<Deadline>(`/deadlines/${encodeURIComponent(deadlineId)}`,
                           { method: 'PATCH', body: patch, signal });
}

/**
 * `GET /reports/health` — is every finished brief actually on this shelf? G12.
 *
 * SILENCE IS A BUG, and `report_sync` was the clearest remaining case in this codebase: it must
 * never raise, so a failure incremented a counter nothing read and logged below the level
 * production emits. What that costs is a finished brief with no route back to it, which is the
 * exact defect the shelf exists to close, happening inside the thing built to close it.
 *
 * The screen asks once, quietly, and says something only when the answer is not zero.
 */
export interface ShelfHealth {
  finished_briefs: number;
  on_the_shelf: number;
  missing: number;
  missing_ids: string[];
  missing_ids_truncated: boolean;
  process_since_start: Record<string, number>;
  note: string;
}

export function reportsHealth(signal?: AbortSignal): Promise<ShelfHealth> {
  return request<ShelfHealth>('/reports/health', { signal });
}

/* --------------------------------------------------------------------------- *
 * Facts — the evidence ledger                                                  *
 * --------------------------------------------------------------------------- */

export interface ListFactsParams {
  subject_type?: FactSubjectType;
  subject_id?: string;
  field?: string;
  status?: FactStatus;
  include_superseded?: boolean;
  limit?: number;
  offset?: number;
}

export function listFacts(params: ListFactsParams = {}, signal?: AbortSignal): Promise<FactPage> {
  return request<FactPage>('/facts', { query: { ...params }, signal });
}

export function getFact(factId: string, signal?: AbortSignal): Promise<Fact> {
  return request<Fact>(`/facts/${encodeURIComponent(factId)}`, { signal });
}

/**
 * `POST /facts` — record an observation and let the server's scorer decide.
 *
 * The client does not choose the score, the band or the status, and it must not
 * pre-judge the outcome in the UI: `stored: false` (evidence below the POSSIBLE
 * floor) is a normal, expected reply and the `rationale` explains it.
 */
export function recordFact(body: NewFact, signal?: AbortSignal): Promise<FactDecision> {
  return request<FactDecision>('/facts', { method: 'POST', body, signal });
}

/** A human accepts a PROPOSED fact. Supersedes the prior APPLIED value. */
export function acceptFact(factId: string, signal?: AbortSignal): Promise<FactDecision> {
  return request<FactDecision>(`/facts/${encodeURIComponent(factId)}/accept`, {
    method: 'POST',
    signal,
  });
}

/** A human rejects a PROPOSED fact. Recorded as DISMISSED, never deleted. */
export function dismissFact(factId: string, signal?: AbortSignal): Promise<FactDecision> {
  return request<FactDecision>(`/facts/${encodeURIComponent(factId)}/dismiss`, {
    method: 'POST',
    signal,
  });
}

/* --------------------------------------------------------------------------- *
 * Queue                                                                        *
 * --------------------------------------------------------------------------- */

export function getQueueStats(signal?: AbortSignal): Promise<QueueStats> {
  return request<QueueStats>('/queue/stats', { signal });
}

/* --------------------------------------------------------------------------- *
 * Reports — the shelf                                                          *
 * --------------------------------------------------------------------------- *
 *
 * A SECOND FLAG GOVERNS THESE. `VALUBRIEF_REPORT_SHELF_ENABLED` (default on) is separate from the
 * CRM's own flag, and `GET /status` reports it as `reports_enabled` whether or not the CRM is on:
 * the shelf answers "can a user see the briefs they made?", which was never in doubt, while the CRM
 * flag governs the parcel/party/engagement model. Listing and reading a report works with the CRM
 * off; FILING one against a parcel does not, because a filing needs a CRM object to point at.
 * A screen must read `status.reports_enabled` rather than assuming either follows the other.
 */

export interface ListReportsParams {
  kind?: string;
  property_key?: string;
  q?: string;
  /** Narrows to the caller's own. A filter, never the default — a colleague must be able to see a
   *  brief their own brokerage paid to produce. */
  mine?: boolean;
  include_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export function listReports(
  params: ListReportsParams = {},
  signal?: AbortSignal,
): Promise<ReportPage> {
  return request<ReportPage>('/reports', { query: { ...params }, signal });
}

export function getReport(reportId: string, signal?: AbortSignal): Promise<Report> {
  return request<Report>(`/reports/${encodeURIComponent(reportId)}`, { signal });
}

/** The ONLY field a user owns on a registry row. Everything else is derived from the report itself
 *  and would go stale or lie the moment it could be edited here. */
export function renameReport(reportId: string, title: string, signal?: AbortSignal): Promise<Report> {
  return request<Report>(`/reports/${encodeURIComponent(reportId)}`, {
    method: 'PATCH', body: { title }, signal,
  });
}

/** A tombstone, not a shredder: `restoreReport` puts it back. */
export function deleteReport(reportId: string, signal?: AbortSignal): Promise<Report> {
  return request<Report>(`/reports/${encodeURIComponent(reportId)}`, { method: 'DELETE', signal });
}

export function restoreReport(reportId: string, signal?: AbortSignal): Promise<Report> {
  return request<Report>(`/reports/${encodeURIComponent(reportId)}/restore`, { method: 'POST', signal });
}

export function listReportLinks(reportId: string, signal?: AbortSignal): Promise<ReportLinkList> {
  return request<ReportLinkList>(`/reports/${encodeURIComponent(reportId)}/links`, { signal });
}

/**
 * File a report against a parcel, an engagement or a party.
 *
 * MANY-TO-MANY ON PURPOSE (router.py): one brief is routinely the evidence in a protest, the comp
 * set for a listing on the same parcel, and the attachment sent to the owner's attorney — three
 * subjects, all true at once. Both ends are re-resolved in the caller's org server-side, so a
 * subject id from another tenant 404s rather than creating a cross-tenant filing.
 */
export function fileReport(
  reportId: string,
  body: NewReportLink,
  signal?: AbortSignal,
): Promise<ReportLink> {
  return request<ReportLink>(`/reports/${encodeURIComponent(reportId)}/links`, {
    method: 'POST', body, signal,
  });
}

/** Unfile. Addressed by subject rather than by link id, because the client removing a filing is
 *  looking at the subject, not at the edge. A hard delete, unlike deleting a report. */
export function unfileReport(
  reportId: string,
  subject_type: ReportSubjectType,
  subject_id: string,
  signal?: AbortSignal,
): Promise<UnlinkResult> {
  return request<UnlinkResult>(`/reports/${encodeURIComponent(reportId)}/links`, {
    method: 'DELETE', query: { subject_type, subject_id }, signal,
  });
}

export function reportHistory(propertyKey: string, signal?: AbortSignal): Promise<ReportHistory> {
  return request<ReportHistory>('/reports/history', { query: { property_key: propertyKey }, signal });
}


/* --------------------------------------------------------------------------- *
 * Reports filed against one subject                                            *
 * --------------------------------------------------------------------------- *
 *
 * THE INVERSE OF `listReportLinks`, AND THE ONE THE UI ACTUALLY WANTS. `listReportLinks`
 * answers "where is this report filed?" — it walks report -> subjects. A drawer open on an
 * engagement is asking the other question: "what has been filed HERE?" These three routes
 * answer it, and the server resolves `view_path` on every row so this client never has to
 * know which store holds which id.
 *
 * `include_deleted` exists because a soft-deleted report is still a document that was handed
 * to somebody. Default false: a list is the live shelf unless someone asks otherwise.
 */
export function reportsForEngagement(
  engagementId: string,
  include_deleted = false,
  signal?: AbortSignal,
): Promise<ReportPage> {
  return request<ReportPage>(
    `/engagements/${encodeURIComponent(engagementId)}/reports`,
    { query: { include_deleted }, signal },
  );
}

/* --------------------------------------------------------------------------- *
 * The tax vertical                                                             *
 * --------------------------------------------------------------------------- */

/**
 * WHAT THIS PROTEST MOVED, and what that movement is worth at the recorded rate.
 *
 * Every figure comes off the `crmn_facts` ledger with the source it was observed from, or it is
 * absent with a sentence saying why — an unaccepted fact, a value that would not parse, a source
 * the analysis cannot cite, or a rate that could be read two ways. The client renders those
 * sentences; it never fills a blank in.
 *
 * 404 is "no such engagement IN YOUR ORG" *and* "the CRM module is switched off", exactly as it is
 * for every other route on this router — see `ApiError`. Do not phrase either as proof that a
 * protest does not exist.
 */
export function protestOutcome(
  engagementId: string,
  signal?: AbortSignal,
): Promise<ProtestOutcome> {
  return request<ProtestOutcome>(
    `/engagements/${encodeURIComponent(engagementId)}/protest-outcome`,
    { signal },
  );
}

/**
 * The whole book, with its denominator attached.
 *
 * `engagementsCounted` says how many engagements the roll-up actually read; the server's page is
 * bounded, and a practice past that bound must be able to see that the total covers part of its
 * book rather than all of it.
 */
export function protestPortfolio(signal?: AbortSignal): Promise<ProtestPortfolio> {
  return request<ProtestPortfolio>('/engagements/protest-outcomes', { signal });
}

export function reportsForParty(
  partyId: string,
  include_deleted = false,
  signal?: AbortSignal,
): Promise<ReportPage> {
  return request<ReportPage>(
    `/parties/${encodeURIComponent(partyId)}/reports`,
    { query: { include_deleted }, signal },
  );
}

export function reportsForParcel(
  parcelId: string,
  include_deleted = false,
  signal?: AbortSignal,
): Promise<ReportPage> {
  return request<ReportPage>(
    `/parcels/${encodeURIComponent(parcelId)}/reports`,
    { query: { include_deleted }, signal },
  );
}

/* --------------------------------------------------------------------------- *
 * AI engine status                                                             *
 * --------------------------------------------------------------------------- *
 *
 * READ-ONLY BY CONSTRUCTION. There is no setter here and there must never be one: which
 * provider a deployment uses is an operator decision made in the environment, not a toggle
 * a browser can flip. A UI that could switch providers could switch a customer onto a model
 * their contract does not cover.
 */
export function getAiStatus(signal?: AbortSignal): Promise<CrmAiStatus> {
  return request<CrmAiStatus>('/ai/status', { signal });
}

/* --------------------------------------------------------------------------- *
 * Board rollups                                                                *
 * --------------------------------------------------------------------------- *
 *
 * The per-column count and exact decimal total for one kind's board. See `StatusRollup` in
 * types.ts for why the total cannot be computed here.
 */
export function engagementRollup(
  kind: EngagementKind,
  signal?: AbortSignal,
): Promise<EngagementRollup> {
  return request<EngagementRollup>('/engagements/rollup', { query: { kind }, signal });
}

/**
 * Ask the background worker to research one engagement.
 *
 * IT RETURNS A RECEIPT, NOT A RESULT. Enrichment is queued onto `crm_next.tasks` — the leased
 * Postgres queue — and whatever it finds is written to the evidence ledger as PROPOSED facts for a
 * human to accept or dismiss. There is no synchronous "here is the dossier" response, because a
 * response like that would be prose composed for the screen rather than a fact with a source.
 *
 * 404 when the deployment has no model configured. The caller reads `/ai/status` first and does not
 * offer the button in that case.
 */
export function requestEnrichment(
  engagementId: string,
  signal?: AbortSignal,
): Promise<{ queued: boolean; task_id: string | null; message: string }> {
  return request<{ queued: boolean; task_id: string | null; message: string }>(
    `/engagements/${encodeURIComponent(engagementId)}/enrich`,
    { method: 'POST', signal },
  );
}


/* --------------------------------------------------------------------------- *
 * Background work — the queue behind the enrichment receipt                     *
 * --------------------------------------------------------------------------- */

/**
 * `GET /tasks` — this org's background work, newest first.
 *
 * WHY THIS EXISTS. `requestEnrichment` returns a `task_id` and until now there was no route, at any
 * layer, to look one up. A receipt for work nobody can find is a control that does nothing.
 */
export function listTasks(
  params: { state?: TaskState; kind?: string; limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<TaskPage> {
  const q = new URLSearchParams();
  if (params.state) q.set('state', params.state);
  if (params.kind) q.set('kind', params.kind);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return request<TaskPage>(`/tasks${qs ? `?${qs}` : ''}`, { signal });
}

/** `GET /tasks/{id}` — one task by the id the enrichment receipt hands back. */
export function getTask(taskId: string, signal?: AbortSignal): Promise<Task> {
  return request<Task>(`/tasks/${encodeURIComponent(taskId)}`, { signal });
}
